import cn from 'classnames';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import Toggle from 'components/toggle';
import { PetContextValues } from 'contexts/pet';
import { usePetProfile } from 'contexts/pet-profile';
import { useSegmentationContext } from 'contexts/segmentation';
import useIsClient from 'hooks/common/use-is-client';
import { possibleTags } from 'interfaces/collection-filter';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSessionStorage } from 'react-use';
import { gtm, selectElement, unselectElement } from 'tracking';
import { isDogProfile } from 'types/pet-profile';
import ProfileButton from './components/profile-button';
import ProfileImage from './components/profile-image';
import ProfileText from './components/profile-text';
import { getPetProfileText } from './utils';

interface Props
  extends React.DetailedHTMLProps<
    React.LabelHTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  location: string;
  temporarySelectedAllergies?: possibleTags[];
  setApplyPetProfile: (value: boolean) => void;
  petType: Exclude<PetContextValues, false>;
  collectionHandle: string;
}

const DEFAULT_VALUE = true;

const TRACKING_NAME = 'Pet Profile Selector';

/**
 * Component used on CDPs that allows the user to create a pet profile, if they don't have one yet.
 * In case they have one, it will show the user's pet attibutes to allow the dynamic selection of products.
 */
const PetProfileCollectionSelector: React.FC<Props> = ({
  className,
  location,
  temporarySelectedAllergies,
  setApplyPetProfile,
  petType,
  collectionHandle,
  ...others
}) => {
  const { isClient } = useIsClient();
  const [toggleActive, setToggleActive] = useSessionStorage<boolean>(
    'pd:pet-profile-selector',
    DEFAULT_VALUE
  );
  const { isReady, query } = useRouter();
  const { formatMessage } = useIntl();
  const { isProfileMinimal, petProfile, updatePetProfile } = usePetProfile();

  const { selectedAge, selectedNeeds, selectedAllergies } = petProfile;
  const selectedBreed = isDogProfile(petProfile)
    ? petProfile.selectedBreed
    : undefined;
  const name = isDogProfile(petProfile) ? petProfile.name : undefined;

  const { getSegmentationFromUrl } = useSegmentationContext();

  const actualSegment = useMemo(() => {
    if (!query.all || !isReady) return;

    return getSegmentationFromUrl({
      url: `/collections/${location}`,
    });
  }, [getSegmentationFromUrl, isReady, location, query.all]);

  useEffect(() => {
    if (isProfileMinimal) {
      setApplyPetProfile(false);
      return;
    }
    if (!actualSegment) {
      setApplyPetProfile(toggleActive);
    }
    return () => {
      setApplyPetProfile(false);
    };
  }, [actualSegment, isProfileMinimal, setApplyPetProfile, toggleActive]);

  const petProfileText = useMemo(
    () =>
      getPetProfileText({
        selectedAge,
        selectedBreed,
        selectedNeeds,
        selectedAllergies,
        formatMessage,
      }),
    [
      selectedAge,
      selectedBreed,
      selectedNeeds,
      selectedAllergies,
      formatMessage,
    ]
  );

  const elementTrackingName = isProfileMinimal
    ? 'Create Profile'
    : actualSegment
      ? 'Edit Profile'
      : 'Toggle Profile';

  return (
    <ImpressionTrackerNew element={[TRACKING_NAME, elementTrackingName]}>
      <div className="relative right-1/2 left-1/2 -mx-[50vw] w-screen lg:static lg:mx-0 lg:w-auto">
        <div
          className={cn(
            'bg-grey-100 mt-0.5 mb-2 flex flex-col px-4 text-center',
            'lg:container lg:mb-5 lg:items-start lg:rounded-xl lg:py-6',
            !isProfileMinimal && !!actualSegment
              ? 'items-start py-4'
              : 'items-center py-5',
            className
          )}
          {...others}
        >
          {isClient && (!!actualSegment || isProfileMinimal) ? (
            <div className="flex w-full flex-col items-center lg:flex-row">
              <ProfileImage
                selectedBreed={selectedBreed}
                size={88}
                petType={petType}
              />
              <ProfileText
                isProfileMinimal={isProfileMinimal}
                petProfileText={petProfileText}
                petType={petType}
              />
              <ProfileButton
                actualSegment={actualSegment}
                isProfileMinimal={isProfileMinimal}
                temporarySelectedAllergies={temporarySelectedAllergies}
                updatePetProfile={updatePetProfile}
                collectionHandle={collectionHandle}
              />
            </div>
          ) : (
            <div className="flex lg:w-full">
              <ProfileImage
                selectedBreed={selectedBreed}
                size={36}
                petType={petType}
              />
              <span className="mr-3 ml-1.5 text-left lg:w-full">
                <span className="text-text-primary mb-0.5 block text-sm leading-4 font-black">
                  {isClient && name ? (
                    <FormattedMessage
                      id="cdp:pet-profile:selector:pet-profile-available:title-with-name"
                      values={{ petName: name }}
                    />
                  ) : (
                    <FormattedMessage
                      id={`cdp:pet-profile:selector:pet-profile-available:title-general-cdp:${petType}`}
                    />
                  )}
                </span>
                {isClient && (
                  <span className="text-text-secondary text-sm leading-4">
                    {petProfileText}
                  </span>
                )}
              </span>
              <Toggle
                dataTest="pet-profile-toggle"
                isChecked={toggleActive}
                showIcon={false}
                onChange={() => {
                  toggleActive
                    ? gtm(
                        unselectElement({
                          element: [
                            TRACKING_NAME,
                            collectionHandle,
                            'Apply Pet Profile',
                          ],
                        })
                      )
                    : gtm(
                        selectElement({
                          element: [
                            TRACKING_NAME,
                            collectionHandle,
                            'Apply Pet Profile',
                          ],
                        })
                      );
                  setToggleActive(!toggleActive);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </ImpressionTrackerNew>
  );
};

export default PetProfileCollectionSelector;
